import React from 'react';
import PropTypes from 'prop-types';
import styles from './RefreshIndicator.module.css';

/**
 * RefreshIndicator - Subtle indicator for background data refresh
 *
 * Features:
 * - Small animated spinner
 * - Optional timestamp
 * - Accessible (screen reader support)
 * - Fade in/out animation
 */
const RefreshIndicator = ({
  isRefreshing = false,
  lastUpdated = null,
  showTimestamp = false,
  timestampFormat = 'absolute'
}) => {
  if (!isRefreshing && !showTimestamp) return null;

  const formatTimestamp = (timestamp, format) => {
    if (!timestamp) return null;

    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return null;
    const pad = (n) => String(n).padStart(2, '0');

    const formatAbsolute = () => {
      const yyyy = d.getFullYear();
      const mm = pad(d.getMonth() + 1);
      const dd = pad(d.getDate());
      const hh = pad(d.getHours());
      const min = pad(d.getMinutes());
      const ss = pad(d.getSeconds());
      return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
    };

    if (format === 'relative') {
      const now = new Date();
      const diffSeconds = Math.floor((now - d) / 1000);
      const diffMinutes = Math.floor(diffSeconds / 60);
      const diffHours = Math.floor(diffSeconds / 3600);

      if (diffSeconds < 10) return 'just now';
      if (diffSeconds < 60) return `${diffSeconds}s ago`;
      if (diffSeconds < 3600) return `${diffMinutes}m ago`;
      if (diffSeconds < 86400) return `${diffHours}h ago`;
      return formatAbsolute();
    }

    return formatAbsolute();
  };

  return (
    <div
      className={styles.container}
      role="status"
      aria-live="polite"
      aria-label={isRefreshing ? 'Refreshing data' : undefined}
    >
      {isRefreshing && (
        <>
          <div className={styles.spinner} aria-hidden="true"></div>
          <span className={styles.text}>Updating...</span>
        </>
      )}

      {!isRefreshing && showTimestamp && lastUpdated && (() => {
        const formatted = formatTimestamp(lastUpdated, timestampFormat);
        return formatted ? (
          <span className={styles.timestamp}>
            Updated at {formatted}
          </span>
        ) : null;
      })()}
    </div>
  );
};

RefreshIndicator.propTypes = {
  isRefreshing: PropTypes.bool,
  lastUpdated: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
  showTimestamp: PropTypes.bool,
  timestampFormat: PropTypes.oneOf(['relative', 'absolute'])
};

export default RefreshIndicator;
